// Add ONE document: the same full pipeline as the bulk import (classify, extract, case, sha256 cache, fail-loud), scoped to
// the signed-in company. Async by nature: upload, then poll the job and surface the REAL outcome (added / duplicate /
// couldn't read), never a fake success. Used by the Cases page and by the header's Upload button, so both behave identically.
//
//   file         the File chosen
//   apiFetch     lib/api apiFetch
//   setMessage   ({ tone: 'info' | 'ok' | 'err', text } | null) => void
//   setBusy      (boolean) => void
//   afterDone    async () => void   called when the job ended (or the wait ran out), so lists can reload
const POLL_MS = 2500;
const WAIT_LIMIT_MS = 5 * 60 * 1000; // stop the UI wait after 5 min; the job keeps running server-side

export async function addDocument({ file, apiFetch, setMessage, setBusy, afterDone = async () => {} }) {
  setBusy(true);
  setMessage({ tone: 'info', text: `Uploading ${file.name}…` });
  let jobId;
  try {
    const fd = new FormData();
    fd.append('files', file);
    const res = await apiFetch('/api/cases/add-document', { method: 'POST', body: fd });
    const json = await res.json();
    if (!res.ok || json.error) throw new Error(json.message || 'Upload failed.');
    jobId = json.jobId;
  } catch (err) {
    setMessage({ tone: 'err', text: err.message });
    setBusy(false);
    setTimeout(() => setMessage(null), 7000);
    return;
  }
  setMessage({ tone: 'info', text: 'Processing document…' });
  pollAddJob({ jobId, apiFetch, setMessage, setBusy, afterDone });
}

async function pollAddJob({ jobId, apiFetch, setMessage, setBusy, afterDone }) {
  const started = Date.now();
  while (Date.now() - started < WAIT_LIMIT_MS) {
    await new Promise((r) => setTimeout(r, POLL_MS));
    let json;
    try {
      const res = await apiFetch(`/api/backfill/batch/${jobId}/detail`);
      json = await res.json();
      if (!res.ok || json.error) throw new Error(json.message || 'Could not read the document.');
    } catch (err) {
      setMessage({ tone: 'err', text: err.message });
      setBusy(false);
      setTimeout(() => setMessage(null), 7000);
      return;
    }
    if (json.done) {
      const s = json.summary || {};
      await afterDone();
      if (s.failed > 0) {
        const why = json.failures && json.failures[0] && json.failures[0].error;
        setMessage({ tone: 'err', text: `Document could not be read${why ? `: ${why}` : '.'}` });
      } else if (s.duplicates > 0 && s.imported === 0) {
        setMessage({ tone: 'info', text: 'Document already on file.' });
      } else {
        setMessage({ tone: 'ok', text: 'Document added.' });
      }
      setBusy(false);
      setTimeout(() => setMessage(null), 7000);
      return;
    }
  }
  await afterDone();
  setMessage({ tone: 'info', text: 'Processing continues. The document will appear when complete.' });
  setBusy(false);
  setTimeout(() => setMessage(null), 9000);
}
